"use client";
import type { PlayingState } from "@ui/components/demos/VideoToMusic/types";

import { Button } from "@ui/components/core/Button";
import { MediaProgressBar } from "@ui/components/core/MediaProgressBar";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import Pause24 from "@ui/icons/24x24/pause-24.svg";
import Play24 from "@ui/icons/24x24/play-24.svg";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

interface MinimalAudioPlayerProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  className?: string;
  onPlay: () => void;
  playingState: PlayingState;
}

export function MinimalAudioPlayer({ audioRef, playingState, onPlay, className }: MinimalAudioPlayerProps) {
  const t = useTranslations("ClientKeys.VideoToMusic");
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateDuration = () => {
      setDuration(audio.duration);
    };

    const updateCurrentTime = () => {
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("durationchange", updateDuration);
    audio.addEventListener("timeupdate", updateCurrentTime);

    return () => {
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("durationchange", updateDuration);
      audio.removeEventListener("timeupdate", updateCurrentTime);
    };
  }, [audioRef]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className={cn("border-gray-200 bg-gray-50 flex w-full items-center gap-12 rounded-lg border p-16", className)}>
      {/* Play/Pause Button */}
      <Button
        className='flex h-40 w-40 items-center justify-center rounded-full p-8'
        disabled={playingState === "loading"}
        onClick={onPlay}
        variant='elevated'
      >
        {playingState === "loading" ? (
          <Spinner20 className='animate-spin' />
        ) : playingState === "playing" ? (
          <Pause24 />
        ) : (
          <Play24 />
        )}
      </Button>

      {/* Progress Bar */}
      <div className='flex flex-1 items-center gap-12'>
        <span className='text-gray-500 min-w-[40px] text-xs'>{formatTime(currentTime)}</span>
        <div className='flex-1'>
          <MediaProgressBar size='lg' mediaRef={audioRef} />
        </div>
        <span className='text-gray-500 min-w-[40px] text-xs'>{formatTime(duration)}</span>
      </div>

      {/* Preview indicator */}
      <div className='border-amber-200 bg-amber-50 flex items-center gap-6 rounded-full border px-8 py-4'>
        <span className='text-xs font-medium'>{t("preview")}</span>
      </div>
    </div>
  );
}
